import React, { useState, useEffect } from 'react';
import { useApiMutation } from "../../../hooks/useMutation";
import { useApiQuery } from "../../../hooks/useApiQuery";
import { toast } from "react-hot-toast";
import { FormData } from '../../../types/Settings';
import { ORG_TYPES, USE_CASES, MONTHLY_VOLUMES } from '../../../utils/Constant';
import Input from '../../../utils/Input';
import Button from '../../../utils/Button';
import { Close, ContentCopy, Mail, Visibility, VisibilityOff } from '@mui/icons-material';
import BYO from './BYO';
import CalculateIcon from '@mui/icons-material/Calculate';

interface MessagingProfile {
  data: {
    smsEnabled: boolean;
    brandStatus: string;
    campaignStatus: string;
    legalName: string;
    brandName: string;
    ein: string;
    profileType: string;
    address: string;
    website: string;
    contactEmail: string;
    contactPhone: string;
    messageSamples: string[];
    optInDescription: string;
    optOutDescription: string;
    submittedAt: string;
    approvedAt: string | null;
    rejectedReason: string | null;
    useCase: string;
    monthlyVolume: string;
    createdAt: string;
    updatedAt: string;
    setupFeePaid: boolean;
    setupFeeAmount: number;
    monthlyComplianceFee: number;
    billingMonth: string;
    smsSegmentsThisMonth: number;
    smsCostThisMonth: number;
    emailCountThisMonth: number;
    emailCostThisMonth: number;
    projectedTotal: number;
  }
}

interface ChargeResponse {
  success: boolean;
  message: string;
  data: {
    message: string;
    checkoutUrl: string;
    reference: string;
  }
}

interface ByoKeysResponse {
  success: boolean;
  message: string;
  data: {
    byoAccountSid: string;
    byoAuthTokenMasked: string;
    byoPhoneNumber: string;
    byoProvider: string;
    byoBrandName: string;
  };
}

const Messaging = () => {
  const [showForm, setShowForm] = useState(false);
  const [showBYOForm, setShowBYOForm] = useState(false);
  const [showBYOCredentialsModal, setShowBYOCredentialsModal] = useState(false);
  const [byoPassword, setByoPassword] = useState("");
  const [showByoPassword, setShowByoPassword] = useState(false);
  const [byoCredentials, setByoCredentials] = useState<ByoKeysResponse["data"] | null>(null);
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [showBillingDetails, setShowBillingDetails] = useState(false);
  const [isCheckingSetupFee, setIsCheckingSetupFee] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [complianceAcknowledged, setComplianceAcknowledged] = useState(false);
  const [sharedMessagingConsent, setSharedMessagingConsent] = useState({
    recipientConsent: false,
    complianceRequirements: false,
    separateBilling: false,
  });
  const [messagingProfile, setMessagingProfile] = useState<MessagingProfile | null>(null);
  const [formData, setFormData] = useState<FormData>({
    legalName: "",
    brandNameForSms: "",
    ein: "",
    address: "",
    website: "",
    contactEmail: "",
    contactPhone: "",
    messageSamples: [
      "Reminder: Your local polling station opens tomorrow at 7AM. Make sure to bring your ID!",
      "Vote123 Alert: Early voting ends this Friday. Don't miss your chance to vote!"
    ],
    optInDescription: "By signing up, you agree to receive important election reminders and voting information via SMS from Vote123.",
    optOutDescription: "Reply STOP to opt-out of receiving election reminders from Vote123.",
    useCase: "",
    monthlyVolume: "",
    organizationType: "",
    complianceConfirmed: false,
  });

  const chargeMutation = useApiMutation<ChargeResponse>({
    onSuccess: (chargeResponse) => {
      const checkoutUrl = chargeResponse?.data?.checkoutUrl;
      if (checkoutUrl) {
        refetchMessagingProfile();
        toast.success("Redirecting you to complete the setup fee");
        window.location.href = checkoutUrl;
      } else {
        toast.error("Payment link unavailable. Please try again.");
      }
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Failed to initiate setup fee payment")
    },
  });

  const byoKeysMutation = useApiMutation<ByoKeysResponse>({
    onSuccess: (response) => {
      setByoCredentials(response.data);
      setByoPassword("");
      toast.success(response.message || "BYO keys retrieved");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to retrieve BYO credentials";
      toast.error(errorMessage);
    },
  });

  const sharedMessagingMutation = useApiMutation({
    onSuccess: () => {
      toast.success("You have been signed into the shared messaging successfully");
      setShowEnableModal(false);
      setSharedMessagingConsent({
        recipientConsent: false,
        complianceRequirements: false,
        separateBilling: false,
      });
      refetchMessagingProfile();
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || "Failed to sign into shared messaging";
      toast.error(errorMessage);
    },
  });

  const submitMutation = useApiMutation({
    onSuccess: () => {
      toast.success("Application submitted, You will be redirected to Payment Portal to complete registration");
      chargeMutation.mutate({
        method: "POST",
        url: "/partner/message/messaging/charge",
      });
      setShowEnableModal(false);
      setCurrentStep(1);
      setComplianceAcknowledged(false);
      refetchMessagingProfile();
      setFormData({
        legalName: "",
        brandNameForSms: "",
        ein: "",
        address: "",
        website: "",
        contactEmail: "",
        contactPhone: "",
        messageSamples: [
          "Reminder: Your local polling station opens tomorrow at 7AM. Make sure to bring your ID!",
          "Vote123 Alert: Early voting ends this Friday. Don't miss your chance to vote!"
        ],
        optInDescription: "By signing up, you agree to receive important election reminders and voting information via SMS from Vote123.",
        optOutDescription: "Reply STOP to opt-out of receiving election reminders from Vote123.",
        useCase: "",
        monthlyVolume: "",
        organizationType: "",
        complianceConfirmed: false,
      });
    },
    onError: () => toast.error("Failed to submit application")
  });

  const { data, refetch: refetchMessagingProfile } = useApiQuery<MessagingProfile>({
    queryKey: ['messaging-profile'],
    request: {
      method: 'GET',
      url: '/partner/messaging/billing/status',
    },
  });

  const messagingProfileData = data?.data
  const isSharedMessagingSignedOn = Boolean(messagingProfileData?.smsEnabled);

  const sharedMessagingReady =
    sharedMessagingConsent.recipientConsent &&
    sharedMessagingConsent.complianceRequirements &&
    sharedMessagingConsent.separateBilling;

  const closeSharedMessagingModal = () => {
    setShowEnableModal(false);
    setSharedMessagingConsent({
      recipientConsent: false,
      complianceRequirements: false,
      separateBilling: false,
    });
  };

  const handleSharedMessagingConsentChange = (
    field: keyof typeof sharedMessagingConsent,
    checked: boolean
  ) => {
    setSharedMessagingConsent((current) => ({
      ...current,
      [field]: checked,
    }));
  };

  const handleSharedMessagingSignOn = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!sharedMessagingReady) {
      toast.error("Please confirm all compliance requirements");
      return;
    }

    setIsCheckingSetupFee(true);

    try {
      const latestBillingStatus = await refetchMessagingProfile();

      if (latestBillingStatus.error) {
        toast.error("Unable to verify messaging setup fee status. Please try again.");
        return;
      }

      const setupFeePaid = latestBillingStatus.data?.data?.setupFeePaid;

      if (setupFeePaid === undefined) {
        toast.error("Messaging setup fee status is unavailable. Please try again.");
        return;
      }

      if (!setupFeePaid) {
        chargeMutation.mutate({
          method: "POST",
          url: "/partner/message/messaging/charge",
        });
        return;
      }

      sharedMessagingMutation.mutate({
        method: "PUT",
        url: "/partner/enabled-modules/messaging-reminder-tools",
        data: {
          enabled: true,
        },
      });
    } finally {
      setIsCheckingSetupFee(false);
    }
  };

  const closeBYOCredentialsModal = () => {
    setShowBYOCredentialsModal(false);
    setByoPassword("");
    setShowByoPassword(false);
    setByoCredentials(null);
  };

  const handleShowBYOCredentials = () => {
    setShowBYOCredentialsModal(true);
    setByoPassword("");
    setShowByoPassword(false);
    setByoCredentials(null);
  };

  const handleFetchBYOCredentials = (e: React.FormEvent) => {
    e.preventDefault();

    if (!byoPassword.trim()) {
      toast.error("Please enter your BYO password");
      return;
    }

    byoKeysMutation.mutate({
      method: "POST",
      url: "/partner/messaging/byo/keys",
      data: {
        password: byoPassword,
      },
    });
  };

  const handleCopyToClipboard = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error(`Failed to copy ${label}`);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (currentStep === 1) {
      // Move to step 2
      setCurrentStep(2);
      return;
    }

    // Step 2 submission
    if (!complianceAcknowledged) {
      toast.error("Please acknowledge messaging compliance requirements");
      return;
    }

    if (!formData.complianceConfirmed) {
      toast.error("Please confirm compliance with carrier and FCC regulations");
      return;
    }

    const submissionData = {
      legalName: formData.legalName,
      brandNameForSms: formData.brandNameForSms,
      ein: formData.ein,
      address: formData.address,
      website: formData.website,
      contactEmail: formData.contactEmail,
      contactPhone: formData.contactPhone,
      messageSamples: formData.messageSamples,
      optInDescription: formData.optInDescription,
      optOutDescription: formData.optOutDescription,
      useCase: formData.useCase,
      monthlyVolume: formData.monthlyVolume,
    };

    submitMutation.mutate({
      method: "POST",
      url: "/partner/messaging/ten-dlc",
      data: submissionData,
    });
  };

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    } else {
      setShowEnableModal(false);
      setCurrentStep(1);
      setComplianceAcknowledged(false);
    }
  };

  const handleMessageSampleChange = (index: number, value: string) => {
    const newSamples = [...formData.messageSamples];
    newSamples[index] = value;
    setFormData(prev => ({ ...prev, messageSamples: newSamples }));
  };

  const handleAddMessageSample = () => {
    setFormData(prev => ({
      ...prev,
      messageSamples: [...prev.messageSamples, ""]
    }));
  };

  const handleRemoveMessageSample = (index: number) => {
    if (formData.messageSamples.length > 1) {
      const newSamples = formData.messageSamples.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, messageSamples: newSamples }));
    }
  };

  if (showForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Complete the form below to register for 10DLC messaging services</h2>
            <p className="text-sm text-gray-500 mt-1">
              123Vote Billing Setup
            </p>
          </div>
          <Button
            onClick={() => setShowForm(false)}
            variant="secondary"
            label='← Back to Providers'
          />
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="bg-gray-50 rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <span className="w-2 h-2 bg-sky-600 rounded-full mr-2"></span>
              Organization Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Input
                  label="Legal Organization Name"
                  value={formData.legalName}
                  placeholder='Must match business documents'
                  onChange={(e) => handleInputChange("legalName", e.target.value)}
                  color="white"
                  size="large"
                  required
                />
              </div>

              <div>
                <Input
                  label="Brand Name for SMS"
                  placeholder="Your brand name"
                  value={formData.brandNameForSms}
                  onChange={(e) => handleInputChange("brandNameForSms", e.target.value)}
                  color="white"
                  size="large"
                  required
                />
              </div>

              <div>
                <Input
                  label="EIN"
                  value={formData.ein}
                  onChange={(e) => handleInputChange("ein", e.target.value)}
                  placeholder="12-3456789"
                  color="white"
                  size="large"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Organization Type
                </label>
                <select
                  value={formData.organizationType}
                  onChange={(e) => handleInputChange("organizationType", e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  required
                >
                  <option value="">Select type</option>
                  {ORG_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <Input
                  label="Address"
                  placeholder='123 xxxx street, xxxx state, USA'
                  value={formData.address}
                  onChange={(e) => handleInputChange("address", e.target.value)}
                  color="white"
                  size="large"
                  required
                />
              </div>

              <div>
                <Input
                  label="Website"
                  type="url"
                  placeholder="https://yourdomain.com"
                  value={formData.website}
                  onChange={(e) => handleInputChange("website", e.target.value)}
                  color="white"
                  size="large"
                  required
                />
              </div>

              <div>
                <Input
                  label="Contact Email"
                  type="email"
                  placeholder="contact@yourdomain.com"
                  value={formData.contactEmail}
                  onChange={(e) => handleInputChange("contactEmail", e.target.value)}
                  color="white"
                  size="large"
                  required
                />
              </div>

              <div>
                <Input
                  label="Contact Phone"
                  type="tel"
                  placeholder='555-123-4567'
                  value={formData.contactPhone}
                  onChange={(e) => handleInputChange("contactPhone", e.target.value)}
                  color="white"
                  size="large"
                  required
                />
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <span className="w-2 h-2 bg-green-600 rounded-full mr-2"></span>
              Messaging Details
            </h3>
            <small className="text-rose-500">Kindly modify the input values with custom texts</small>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Use Case
                </label>
                <select
                  value={formData.useCase}
                  onChange={(e) => handleInputChange("useCase", e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  required
                >
                  <option value="">Select use case</option>
                  {USE_CASES.map((useCase) => (
                    <option key={useCase.value} value={useCase.value}>
                      {useCase.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Monthly Volume
                </label>
                <select
                  value={formData.monthlyVolume}
                  onChange={(e) => handleInputChange("monthlyVolume", e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  required
                >
                  <option value="">Select volume</option>
                  {MONTHLY_VOLUMES.map((volume) => (
                    <option key={volume.value} value={volume.value}>
                      {volume.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-4 bg-gray-200 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Message Samples
                </label>
                <Button
                  onClick={handleAddMessageSample}
                  variant="secondary"
                  label="+ Add Sample"
                  size="medium"
                />
              </div>
              {formData.messageSamples.map((sample, index) => (
                <div key={index} className="mb-3 flex w-full items-center gap-5">
                  <Input
                    value={sample}
                    onChange={(e) => handleMessageSampleChange(index, e.target.value)}
                    placeholder={`Enter message sample ${index + 1}`}
                    color="white"
                    size="large"
                    required
                    className="w-full "
                  />
                  {formData.messageSamples.length > 1 && (
                    <Button
                      onClick={() => handleRemoveMessageSample(index)}
                      variant="tertiary"
                      icon={<Close />}
                      size="small"
                    />
                  )}
                </div>
              ))}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Opt-In Description
              </label>
              <textarea
                value={formData.optInDescription}
                onChange={(e) => handleInputChange("optInDescription", e.target.value)}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Opt-Out Description
              </label>
              <textarea
                value={formData.optOutDescription}
                onChange={(e) => handleInputChange("optOutDescription", e.target.value)}
                rows={2}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                required
              />
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Compliance</h3>
            <p className="text-sm text-gray-700 mb-4">
              You agree to send only civic/election content and honor opt-outs per carrier requirements.
            </p>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="compliance"
                checked={formData.complianceConfirmed}
                onChange={(e) => handleInputChange("complianceConfirmed", e.target.checked)}
                className="w-4 h-4 text-sky-600 border-gray-300 rounded focus:ring-blue-500"
                required
              />
              <label htmlFor="compliance" className="ml-2 text-sm text-gray-700">
                I confirm compliance with carrier and FCC regulations
              </label>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              type="submit"
              variant='primary'
              disabled={submitMutation.isPending}
              label={submitMutation.isPending ? "Submitting..." : currentStep === 1 ? "Next" : "Enable & Register"}
            />

          </div>
        </form>
      </div>
    );
  }

  if (showEnableModal) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
        <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white">
          <div className="border-b border-gray-200 p-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Shared 123-Vote Messaging</h2>
                <p className="mt-1 text-sm text-gray-500">
                  Review pricing and compliance requirements before signing on.
                </p>
              </div>
              <Button
                onClick={closeSharedMessagingModal}
                variant="secondary"
                label="Close"
                size="medium"
              />
            </div>
          </div>

          <div className="p-6">
            <form onSubmit={handleSharedMessagingSignOn} className="space-y-6">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-5">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">Fee structure</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between border-b border-gray-200 pb-3">
                    <span className="text-sm text-gray-700">One-time setup fee</span>
                    <span className="font-bold text-lg text-red-900 ">$15.00</span>
                  </div>
                  <div className="flex items-center justify-between border-b border-gray-200 pb-3">
                    <span className="text-sm text-gray-700">Monthly Compliance Fee</span>
                    <span className="font-semibold text-gray-900">$2.00 per month</span>
                  </div>
                  <div className="flex items-center justify-between border-b border-gray-200 pb-3">
                    <span className="text-sm text-gray-700">SMS pricing</span>
                    <span className="font-semibold text-gray-900">$0.015 per message</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Email pricing</span>
                    <span className="font-semibold text-gray-900">$0.03 per message</span>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-5">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">Compliance structure</h3>
                <div className="space-y-4">
                  <label className="flex items-start gap-3 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={sharedMessagingConsent.recipientConsent}
                      onChange={(e) => handleSharedMessagingConsentChange("recipientConsent", e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-blue-500"
                    />
                    <span>Organization confirms all SMS recipients provide consent.</span>
                  </label>
                  <label className="flex items-start gap-3 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={sharedMessagingConsent.complianceRequirements}
                      onChange={(e) => handleSharedMessagingConsentChange("complianceRequirements", e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-blue-500"
                    />
                    <span>Organization agrees to follow <a href="/terms-platform" target="_blank" className="text-blue-500 hover:underline">messaging compliance requirements.</a></span>
                  </label>
                  <label className="flex items-start gap-3 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={sharedMessagingConsent.separateBilling}
                      onChange={(e) => handleSharedMessagingConsentChange("separateBilling", e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-blue-500"
                    />
                    <span>Organization understands messaging usage is billed separately.</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  onClick={closeSharedMessagingModal}
                  variant="secondary"
                  label="Cancel"
                  size="medium"
                />
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isCheckingSetupFee || sharedMessagingMutation.isPending || chargeMutation.isPending || !sharedMessagingReady}
                  label={
                    isCheckingSetupFee
                      ? "Checking..."
                      : chargeMutation.isPending
                      ? "Redirecting..."
                      : sharedMessagingMutation.isPending
                        ? "Signing on..."
                        : "Sign on"
                  }
                  size="medium"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (showBYOForm) {
    return <BYO setShowBYOForm={setShowBYOForm} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">

        <div className="text-left mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">SMS Messaging Setup</h2>
          <p className="text-gray-600">Choose your SMS provider to get started with messaging</p>
        </div>

        {messagingProfileData?.profileType === "BYO" && (
          <Button
            variant="secondary"
            onClick={handleShowBYOCredentials}
            label="Show BYO credentials"
          />
        )}

      </div>

      {showBYOCredentialsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">BYO credentials</h3>
                <p className="mt-1 text-sm text-gray-600">
                  Enter your BYO password to retrieve provider credentials.
                </p>
              </div>
              <button
                type="button"
                onClick={closeBYOCredentialsModal}
                className="rounded-full p-1 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close BYO credentials"
              >
                <Close fontSize="small" />
              </button>
            </div>

            {!byoCredentials ? (
              <form onSubmit={handleFetchBYOCredentials} className="space-y-5">
                <div>
                  <label htmlFor="byo-password" className="mb-2 block text-sm font-medium text-gray-700">
                    BYO password
                  </label>
                  <div className="relative">
                    <input
                      id="byo-password"
                      type={showByoPassword ? "text" : "password"}
                      value={byoPassword}
                      onChange={(e) => setByoPassword(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-3 pr-11 text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      placeholder="Enter password"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowByoPassword((current) => !current)}
                      className="absolute inset-y-0 right-2 flex items-center rounded-md px-2 text-gray-500 transition hover:text-gray-800"
                      aria-label={showByoPassword ? "Hide password" : "Show password"}
                    >
                      {showByoPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="secondary"
                    size="medium"
                    label="Cancel"
                    onClick={closeBYOCredentialsModal}
                  />
                  <Button
                    type="submit"
                    variant="primary"
                    size="medium"
                    disabled={byoKeysMutation.isPending}
                    label={byoKeysMutation.isPending ? "Retrieving..." : "Retrieve"}
                  />
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <p className="text-xs font-semibold uppercase text-blue-700">Provider</p>
                  <p className="mt-1 text-2xl font-bold capitalize text-blue-900">
                    {byoCredentials.byoProvider}
                  </p>
                </div>

                <div className="grid gap-3">
                  <div className="rounded-lg border border-gray-200 p-4">
                    <p className="text-xs font-medium uppercase text-gray-500">Account SID</p>
                    <p className="mt-1 break-all text-sm font-semibold text-gray-900">
                      {byoCredentials.byoAccountSid}
                    </p>
                  </div>

                  <div className="rounded-lg border border-gray-200 p-4">
                    <p className="text-xs font-medium uppercase text-gray-500">Auth token</p>
                    <p className="mt-1 break-all text-sm font-semibold text-gray-900">
                      {byoCredentials.byoAuthTokenMasked}
                    </p>
                  </div>

                  <div className="rounded-lg border border-gray-200 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs font-medium uppercase text-gray-500">Brand name</p>
                        <p className="mt-1 break-all text-sm font-semibold text-gray-900">
                          {byoCredentials.byoBrandName}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleCopyToClipboard(byoCredentials.byoBrandName, "Brand name")}
                        className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs font-medium text-gray-700 transition hover:bg-gray-50"
                      >
                        <ContentCopy fontSize="inherit" />
                        Copy
                      </button>
                    </div>
                  </div>

                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-xs font-medium uppercase text-emerald-700">Phone number</p>
                        <p className="mt-1 break-all text-lg font-bold text-emerald-900">
                          {byoCredentials.byoPhoneNumber}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleCopyToClipboard(byoCredentials.byoPhoneNumber, "Phone number")}
                        className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-white px-2 py-1 text-xs font-medium text-emerald-800 transition hover:bg-emerald-50"
                      >
                        <ContentCopy fontSize="inherit" />
                        Copy
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="secondary"
                    size="medium"
                    label="Close"
                    onClick={closeBYOCredentialsModal}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {messagingProfileData && messagingProfileData.profileType === "BYO" && (
        <div className="w-full mx-auto mb-8">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-semibold text-blue-900">Application Status</h4>
                <div className="mt-2 space-y-1">
                  <h3 className="font-medium">Profile Type: {messagingProfileData.profileType}</h3>
                  <div className="flex items-center">
                    <span className="text-xs text-gray-600 w-24">Brand Status:</span>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${messagingProfileData.brandStatus === 'APPROVED' ? 'bg-green-100 text-green-800' :
                      messagingProfileData.brandStatus === 'PROCESSING' ? 'bg-yellow-100 text-yellow-800' :
                        messagingProfileData.brandStatus === 'REJECTED' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                      }`}>
                      {messagingProfileData.brandStatus}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-xs text-gray-600 w-24">Campaign:</span>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${messagingProfileData.campaignStatus === 'APPROVED' ? 'bg-green-100 text-green-800' :
                      messagingProfileData.campaignStatus === 'PROCESSING' ? 'bg-yellow-100 text-yellow-800' :
                        messagingProfileData.campaignStatus === 'REJECTED' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                      }`}>
                      {messagingProfileData.campaignStatus}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-xs text-gray-600 w-24">SMS Enabled:</span>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${messagingProfileData.smsEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                      {messagingProfileData.smsEnabled ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div className="mt-3">
                    <button
                      onClick={() => setShowBillingDetails(!showBillingDetails)}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      {showBillingDetails ? 'Hide Details' : 'View All'}
                    </button>
                  </div>
                  {showBillingDetails && (
                    <div className="mt-4 space-y-2 border-t border-gray-200 pt-4">
                      <div className="flex items-center">
                        <span className="text-xs text-gray-600 w-32">Setup Fee Paid:</span>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${messagingProfileData.setupFeePaid ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                          {messagingProfileData.setupFeePaid ? 'Yes' : 'No'}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-xs text-gray-600 w-32">Setup Fee Amount:</span>
                        <span className="text-xs font-medium text-gray-900">
                          ${messagingProfileData.setupFeeAmount}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-xs text-gray-600 w-32">Monthly Compliance:</span>
                        <span className="text-xs font-medium text-gray-900">
                          ${messagingProfileData.monthlyComplianceFee}/month
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-xs text-gray-600 w-32">Billing Month:</span>
                        <span className="text-xs font-medium text-gray-900">
                          {messagingProfileData.billingMonth}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-xs text-gray-600 w-32">SMS Segments:</span>
                        <span className="text-xs font-medium text-gray-900">
                          {messagingProfileData.smsSegmentsThisMonth}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-xs text-gray-600 w-32">SMS Cost:</span>
                        <span className="text-xs font-medium text-gray-900">
                          ${messagingProfileData.smsCostThisMonth}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-xs text-gray-600 w-32">Email Count:</span>
                        <span className="text-xs font-medium text-gray-900">
                          {messagingProfileData.emailCountThisMonth}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-xs text-gray-600 w-32">Email Cost:</span>
                        <span className="text-xs font-medium text-gray-900">
                          ${messagingProfileData.emailCostThisMonth}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-xs text-gray-600 w-32">Projected Total:</span>
                        <span className="text-xs font-semibold text-gray-900">
                          ${messagingProfileData.projectedTotal}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {messagingProfileData?.submittedAt && (
                <div className="text-right">
                  <p className="text-xs text-gray-500">Submitted</p>
                  <p className="text-xs font-medium text-gray-700">
                    {new Date(messagingProfileData.submittedAt).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
            {messagingProfileData?.rejectedReason && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                <p className="text-xs font-medium text-red-800">Rejection Reason:</p>
                <p className="text-xs text-red-700 mt-1">{messagingProfileData?.rejectedReason}</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6  mx-auto">
        <div
          onClick={() => {

            if (isSharedMessagingSignedOn) {
              toast.success("The shared messaging module has been enabled by your organisation")
              return
            }
            setShowEnableModal(true);
          }}
          className="bg-white border border-slate-300 hover:border-2 hover:border-blue-500 rounded-lg p-6 cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
        >
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
              <span className="text-2xl"><Mail /></span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Shared 123-Vote Messaging</h3>
              <p className="text-sm text-gray-500">Use 123-Vote Messaging Infrastructure</p>
            </div>
          </div>
         {isSharedMessagingSignedOn && ( <p className="text-green-800 py-2 text-sm">This service has already been enabled by your organisation</p>)}

          <p className="text-gray-700 mb-4">
            Use 123-Vote’s pre-approved messaging infrastructure for faster onboarding and simplified setup. </p>

          <ul className="space-y-2 text-sm text-gray-600">
            
            <li className="flex items-center">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
              Carrier compliance handled
            </li>
            <li className="flex items-center">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
              Simple billing through 123Vote
            </li>
          </ul>
        </div>

        {/* Bring Your Own Provider Option */}
        <div
          onClick={() => setShowBYOForm(true)}
          className="bg-white border border-slate-300 hover:border-2 hover:border-blue-500 rounded-lg p-6 cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105"
        >
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mr-4">
              <span className="text-2xl"><Mail /></span>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Bring Your Own Provider</h3>
              <p className="text-sm text-gray-500">Use your existing SMS service</p>
            </div>
          </div>
          <p className="text-gray-700 mb-4">
            Connect your existing SMS provider to our platform. Ideal for organizations with established messaging infrastructure.
          </p>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-center">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
              Use your preferred SMS provider
            </li>
            <li className="flex items-center">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
              Maintain existing relationships
            </li>
            <li className="flex items-center">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
              Flexible integration options
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Messaging;
